import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../services/apiService';

const ConfigSettings = ({ showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [countries, setCountries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [countryChannels, setCountryChannels] = useState([]);
  const [campaignChannels, setCampaignChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [pendingCountryChanges, setPendingCountryChanges] = useState({});
  const [pendingCampaignChanges, setPendingCampaignChanges] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Estados para gestión de canales
  const [openChannelDialog, setOpenChannelDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelFormData, setChannelFormData] = useState({
    channel_name: '',
    channel_type: '',
    description: ''
  });
  const [savingChannel, setSavingChannel] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [deletingChannel, setDeletingChannel] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, channelsRes, campaignsRes, countryChannelsRes, campaignChannelsRes] = await Promise.all([
        apiService.getCountries(),
        apiService.getChannels(),
        apiService.getCampaigns(),
        apiService.getCountryChannels(),
        apiService.getCampaignChannels()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (channelsRes.success) setChannels(channelsRes.channels);
      if (campaignsRes.success) setCampaigns(campaignsRes.campaigns);
      if (countryChannelsRes.success) setCountryChannels(countryChannelsRes.country_channels);
      if (campaignChannelsRes.success) setCampaignChannels(campaignChannelsRes.campaign_channels);
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  const getFilteredCountries = () => {
    if (selectedRegion === 'all') return countries;
    
    const regions = {
      'cono_sur': ['AR', 'CL', 'UY', 'PY', 'VE', 'PE', 'CO', 'EC', 'BO'],
      'centro_america': ['MX', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA', 'DO']
    };
    
    const regionCountries = regions[selectedRegion] || [];
    return countries.filter(country => regionCountries.includes(country.code));
  };

  const isCountryChannelActive = (countryCode, channelId) => {
    const mapping = countryChannels.find(
      cc => cc.country_code === countryCode && cc.channel_id === channelId
    );
    
    // Verificar si hay cambio pendiente
    const key = `${countryCode}-${channelId}`;
    if (pendingCountryChanges.hasOwnProperty(key)) {
      return pendingCountryChanges[key];
    }
    
    return mapping?.is_active || false;
  };

  const handleCountryChannelToggle = (countryCode, channelId, checked) => {
    const key = `${countryCode}-${channelId}`;
    setPendingCountryChanges(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSaveCountryChannels = async () => {
    try {
      setSaving(true);
      const changes = Object.entries(pendingCountryChanges);
      
      for (const [key, isActive] of changes) {
        const [countryCode, channelId] = key.split('-');
        await apiService.updateCountryChannel({
          country_code: countryCode,
          channel_id: channelId,
          is_active: isActive
        });
      }
      
      setPendingCountryChanges({});
      await loadData();
      showSnackbar('Canales por país actualizados correctamente');
    } catch (err) {
      showSnackbar('Error al guardar cambios', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isCampaignChannelActive = (campaignId, channelId) => {
    const mapping = campaignChannels.find(
      cc => cc.campaign_id === campaignId && cc.channel_id === channelId
    );
    
    // Verificar si hay cambio pendiente
    const key = `${campaignId}-${channelId}`;
    if (pendingCampaignChanges.hasOwnProperty(key)) {
      return pendingCampaignChanges[key];
    }
    
    return mapping?.is_active || false;
  };

  const handleCampaignChannelToggle = (campaignId, channelId, checked) => {
    const key = `${campaignId}-${channelId}`;
    setPendingCampaignChanges(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSaveCampaignChannels = async () => {
    try {
      setSaving(true);
      const changes = Object.entries(pendingCampaignChanges);
      
      for (const [key, isActive] of changes) {
        const [campaignId, channelId] = key.split('-');
        await apiService.updateCampaignChannel({
          campaign_id: campaignId,
          channel_id: channelId,
          is_active: isActive
        });
      }
      
      setPendingCampaignChanges({});
      await loadData();
      showSnackbar('Canales por ToolBox actualizados correctamente');
    } catch (err) {
      showSnackbar('Error al guardar cambios', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const filteredCountries = getFilteredCountries();
  const hasPendingCountryChanges = Object.keys(pendingCountryChanges).length > 0;
  const hasPendingCampaignChanges = Object.keys(pendingCampaignChanges).length > 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">
          Configuración
        </Typography>
      </Box>

      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
          >
            <Tab label="Canales por País" />
            <Tab label="Canales por ToolBox" />
            <Tab label="Gestión de Canales" />
          </Tabs>
        </Box>

        {/* Tab 1: Canales por País */}
        {activeTab === 0 && (
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box 
              display="flex" 
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="space-between" 
              alignItems={isMobile ? 'flex-start' : 'center'} 
              sx={{ mb: 2, gap: { xs: 2, sm: 0 } }}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel>Filtrar por Región</InputLabel>
                <Select
                  value={selectedRegion}
                  label="Filtrar por Región"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <MenuItem value="all">Todas las Regiones</MenuItem>
                  <MenuItem value="cono_sur">Cono Sur</MenuItem>
                  <MenuItem value="centro_america">Centro América</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCountryChannels}
                disabled={!hasPendingCountryChanges || saving}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'large'}
                sx={{ minWidth: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar Cambios'}
              </Button>
            </Box>
            {hasPendingCountryChanges && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Tienes {Object.keys(pendingCountryChanges).length} cambio(s) pendiente(s)
              </Alert>
            )}
            <Divider sx={{ mb: 2 }} />
            <TableContainer 
              sx={{ 
                maxHeight: { xs: 400, sm: 600 },
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 650
                }
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150, color: 'white', backgroundColor: '#004a8F' }}>País</TableCell>
                    {channels.map((channel) => (
                      <TableCell key={channel.id} align="center" sx={{ fontWeight: 'bold', minWidth: 120, color: 'white', backgroundColor: '#004a8F' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                            {channel.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {channel.type}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCountries.map((country) => (
                    <TableRow key={country.code} hover>
                      <TableCell component="th" scope="row">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {country.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {country.code}
                          </Typography>
                        </Box>
                      </TableCell>
                      {channels.map((channel) => (
                        <TableCell key={`${country.code}-${channel.id}`} align="center">
                          <Checkbox
                            checked={isCountryChannelActive(country.code, channel.id)}
                            onChange={(e) => handleCountryChannelToggle(country.code, channel.id, e.target.checked)}
                            color="primary"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 2: Canales por Campaña */}
        {activeTab === 1 && (
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box display="flex" justifyContent={isMobile ? 'stretch' : 'flex-end'} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCampaignChannels}
                disabled={!hasPendingCampaignChanges || saving}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'large'}
                sx={{ minHeight: 44 }}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar Cambios'}
              </Button>
            </Box>
            {hasPendingCampaignChanges && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Tienes {Object.keys(pendingCampaignChanges).length} cambio(s) pendiente(s)
              </Alert>
            )}
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {campaigns.map((campaign) => (
                <Grid item xs={12} md={6} key={campaign.id}>
                  <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} sx={{ mb: 2, gap: { xs: 1, sm: 0 } }}>
                      <Box>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>{campaign.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.type}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${campaignChannels.filter(cc => cc.campaign_id === campaign.id && cc.is_active).length} canales`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={{ xs: 1, sm: 1 }}>
                      {channels.map((channel) => (
                        <Grid item xs={12} sm={6} md={4} key={channel.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isCampaignChannelActive(campaign.id, channel.id)}
                                onChange={(e) => handleCampaignChannelToggle(campaign.id, channel.id, e.target.checked)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {channel.name}
                              </Typography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 3: Gestión de Canales */}
        {activeTab === 2 && (
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              sx={{ mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}
            >
              <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                Gestión de Canales
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingChannel(null);
                  setChannelFormData({
                    channel_name: '',
                    channel_type: '',
                    description: ''
                  });
                  setOpenChannelDialog(true);
                }}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'large'}
                sx={{ minHeight: 44 }}
              >
                Nuevo Canal
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer 
              component={Paper}
              sx={{ 
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: 600
                }
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {channels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No hay canales registrados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    channels.map((channel) => (
                      <TableRow key={channel.id} hover>
                        <TableCell>{channel.id}</TableCell>
                        <TableCell>{channel.name}</TableCell>
                        <TableCell>{channel.type || '-'}</TableCell>
                        <TableCell>{channel.description || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingChannel(channel);
                              setChannelFormData({
                                channel_name: channel.name,
                                channel_type: channel.type || '',
                                description: channel.description || ''
                              });
                              setOpenChannelDialog(true);
                            }}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setChannelToDelete(channel);
                              setOpenDeleteDialog(true);
                            }}
                            sx={{ minWidth: 44, minHeight: 44, color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Diálogo para crear/editar canal */}
      <Dialog
        open={openChannelDialog}
        onClose={() => !savingChannel && setOpenChannelDialog(false)}
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {editingChannel ? 'Editar Canal' : 'Crear Nuevo Canal'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          {editingChannel && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                ID del Canal (no editable)
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {editingChannel.id}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Nombre del Canal"
            value={channelFormData.channel_name}
            onChange={(e) => setChannelFormData({...channelFormData, channel_name: e.target.value})}
            required
            sx={{ mb: 2 }}
            margin="normal"
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tipo de Canal</InputLabel>
            <Select
              value={channelFormData.channel_type}
              label="Tipo de Canal"
              onChange={(e) => setChannelFormData({...channelFormData, channel_type: e.target.value})}
            >
              <MenuItem value="">Sin tipo</MenuItem>
              <MenuItem value="Red Social">Red Social</MenuItem>
              <MenuItem value="Publicidad">Publicidad</MenuItem>
              <MenuItem value="Video">Video</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="Mensajería">Mensajería</MenuItem>
              <MenuItem value="Marketing">Marketing</MenuItem>
              <MenuItem value="Punto de Venta">Punto de Venta</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Descripción"
            value={channelFormData.description}
            onChange={(e) => setChannelFormData({...channelFormData, description: e.target.value})}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={() => {
              setOpenChannelDialog(false);
              setEditingChannel(null);
              setChannelFormData({
                channel_name: '',
                channel_type: '',
                description: ''
              });
            }}
            disabled={savingChannel}
            fullWidth={isMobile}
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (!channelFormData.channel_name.trim()) {
                showSnackbar('El nombre del canal es requerido', 'error');
                return;
              }

              try {
                setSavingChannel(true);
                if (editingChannel) {
                  // Actualizar canal existente
                  const response = await apiService.updateChannel(editingChannel.id, {
                    channel_name: channelFormData.channel_name.trim(),
                    channel_type: channelFormData.channel_type.trim() || null,
                    description: channelFormData.description.trim() || null
                  });
                  
                  if (response.success) {
                    showSnackbar('Canal actualizado correctamente');
                    setOpenChannelDialog(false);
                    setEditingChannel(null);
                    setChannelFormData({
                      channel_name: '',
                      channel_type: '',
                      description: ''
                    });
                    await loadData();
                  }
                } else {
                  // Crear nuevo canal
                  const response = await apiService.createChannel({
                    channel_name: channelFormData.channel_name.trim(),
                    channel_type: channelFormData.channel_type.trim() || null,
                    description: channelFormData.description.trim() || null
                  });
                  
                  if (response.success) {
                    showSnackbar('Canal creado correctamente');
                    setOpenChannelDialog(false);
                    setChannelFormData({
                      channel_name: '',
                      channel_type: '',
                      description: ''
                    });
                    await loadData();
                  }
                }
              } catch (err) {
                showSnackbar('Error al guardar el canal', 'error');
              } finally {
                setSavingChannel(false);
              }
            }}
            variant="contained"
            disabled={savingChannel || !channelFormData.channel_name.trim()}
            fullWidth={isMobile}
            startIcon={savingChannel ? <CircularProgress size={20} /> : null}
          >
            {savingChannel ? 'Guardando...' : (editingChannel ? 'Guardar Cambios' : 'Crear Canal')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar canal */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deletingChannel && setOpenDeleteDialog(false)}
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            Eliminar Canal
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar el canal <strong>"{channelToDelete?.name}"</strong> (ID: {channelToDelete?.id})?
            <br /><br />
          </DialogContentText>
          {channelToDelete?.in_use && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              Este canal está siendo usado en: {channelToDelete.usage_tables?.join(', ')}. 
              La eliminación puede afectar datos relacionados.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setChannelToDelete(null);
            }}
            disabled={deletingChannel}
            fullWidth={isMobile}
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (!channelToDelete) return;
              
              try {
                setDeletingChannel(true);
                const response = await apiService.deleteChannel(channelToDelete.id);
                
                if (response.success) {
                  showSnackbar(response.message || 'Canal eliminado correctamente');
                  setOpenDeleteDialog(false);
                  setChannelToDelete(null);
                  await loadData();
                } else {
                  // Si el error incluye información de uso, actualizar el estado
                  if (response.in_use) {
                    setChannelToDelete({
                      ...channelToDelete,
                      in_use: true,
                      usage_tables: response.usage_tables || []
                    });
                  }
                  showSnackbar(response.error || 'Error al eliminar el canal', 'error');
                }
              } catch (err) {
                // Manejar errores de respuesta HTTP (400, 404, etc.)
                if (err.response?.data) {
                  const errorData = err.response.data;
                  if (errorData.in_use) {
                    setChannelToDelete({
                      ...channelToDelete,
                      in_use: true,
                      usage_tables: errorData.usage_tables || []
                    });
                    showSnackbar(errorData.error || 'El canal está en uso y no se puede eliminar', 'error');
                  } else {
                    showSnackbar(errorData.error || 'Error al eliminar el canal', 'error');
                  }
                } else {
                  showSnackbar('Error al eliminar el canal', 'error');
                }
              } finally {
                setDeletingChannel(false);
              }
            }}
            color="error"
            variant="contained"
            disabled={deletingChannel}
            fullWidth={isMobile}
            startIcon={deletingChannel ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingChannel ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigSettings;
